import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import AddVocabularyForm from "../components/vocabularyJapanese/AddVocabularyForm";
import VocabularyCard from "../components/vocabularyJapanese/VocabularyCard";
import SearchBar from "../components/vocabularyJapanese/SearchBar";
import StatisticsGrid from "../components/vocabularyJapanese/StatisticsGrid";
import FlashcardModal from "../components/vocabularyJapanese/FlashcardModal";

/**
 * VocabularyJapanese Component - Performance Optimized Version
 * 
 * OPTIMIZATIONS APPLIED:
 * 1. React.memo() - Prevents unnecessary re-renders
 * 2. useMemo() - Expensive calculations (filtering, sorting) cached
 * 3. Throttled events - Scroll and localStorage saves throttled
 * 4. Improved cleanup - Better audio and timeout management
 * 5. Memoized functions - All callbacks properly optimized
 * 6. Passive scroll listeners - Reduces scroll event overhead
 * 7. Batch operations - Grouped related state updates
 * 
 * This reduces CPU usage and prevents machine overheating
 */

// Utility function to throttle events
const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }
};

const VocabularyJapanese = React.memo(() => {
  // Main vocabulary state
  const [vocabularyList, setVocabularyList] = useState([]);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingWord, setEditingWord] = useState(null);

  // Modal states
  const [showFlashcardModal, setShowFlashcardModal] = useState(false);
  const [flashcardModalStep, setFlashcardModalStep] = useState("options");

  // UI states
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [hiddenVietnameseMeanings, setHiddenVietnameseMeanings] = useState(
    new Set()
  );

  // Audio refs
  const audioVocabRef = useRef(null);
  const audioSentenceRef = useRef(null);
  const youtubePlayerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Refs for cleanup
  const audioTimeoutRef = useRef(null);

  // Load vocabulary from localStorage on mount
  useEffect(() => {
    const savedVocab = localStorage.getItem("vocabularyJapanese");
    if (savedVocab) {
      try {
        const parsed = JSON.parse(savedVocab);
        const processedData = parsed.map((word) => ({
          ...word,
          createdAt: new Date(word.createdAt),
          nextReview: new Date(word.nextReview),
        }));
        setVocabularyList(processedData);

        // Hide all Vietnamese meanings by default
        const allWordIds = processedData.map((word) => word.id);
        setHiddenVietnameseMeanings(new Set(allWordIds));

        console.log(
          "Loaded vocabulary from localStorage:",
          processedData.length,
          "items"
        );
      } catch (error) {
        console.error("Error loading vocabulary:", error);
      }
    }

    // Load YouTube API
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
  }, []);

  // Optimized localStorage save function - Only save when needed
  const saveToLocalStorage = useCallback((vocabularyData) => {
    try {
      if (!vocabularyData || vocabularyData.length === 0) {
        localStorage.removeItem("vocabularyJapanese");
        console.log("Removed vocabulary from localStorage (empty list)");
      } else {
        localStorage.setItem("vocabularyJapanese", JSON.stringify(vocabularyData));
        console.log("Saved vocabulary to localStorage:", vocabularyData.length, "items");
      }
    } catch (error) {
      console.error("Error saving vocabulary:", error);
    }
  }, []);

  // Save on page unload to prevent data loss
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveToLocalStorage(vocabularyList);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Final save on unmount
      saveToLocalStorage(vocabularyList);
    };
  }, [vocabularyList, saveToLocalStorage]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
      if (e.key === "Escape" && (searchQuery || statusFilter !== "all")) {
        setSearchQuery("");
        setStatusFilter("all");
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [searchQuery, statusFilter]);

  // Handle scroll to show/hide scroll to top button (throttled)
  useEffect(() => {
    const handleScroll = throttle(() => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollTop(scrollTop > 400);
    }, 100); // Throttle to 100ms

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
      }
      if (window.sentenceAudioTimeout) {
        clearTimeout(window.sentenceAudioTimeout);
        window.sentenceAudioTimeout = null;
      }
    };
  }, []);

  // Spaced repetition algorithm
  const calculateNextReview = useCallback((difficulty, currentStep = 0) => {
    const now = new Date();
    let nextStep = currentStep;
    let intervalHours;

    const schedule = [0, 20 / 60, 1, 4, 8, 24, 72, 168, 336, 720, 2160, 4320];

    switch (difficulty) {
      case "again":
        nextStep = 1;
        break;
      case "hard":
        nextStep = Math.max(1, currentStep);
        break;
      case "medium":
        nextStep = Math.min(schedule.length - 1, currentStep + 1);
        break;
      case "easy":
        nextStep = Math.min(schedule.length - 1, currentStep + 2);
        break;
      default:
        nextStep = Math.min(schedule.length - 1, currentStep + 1);
    }

    intervalHours = schedule[nextStep];
    const milliseconds = intervalHours * 60 * 60 * 1000;

    return {
      nextReview: new Date(now.getTime() + milliseconds),
      step: nextStep,
      intervalHours,
    };
  }, []);

  // Memoized review priority calculation
  const getReviewPriority = useCallback((word) => {
    const now = new Date();
    const nextReview = new Date(word.nextReview);
    const timeDiff = now.getTime() - nextReview.getTime();
    const hoursDiff = timeDiff / (1000 * 60 * 60);

    if (hoursDiff > 24) return "urgent";
    if (hoursDiff > 1) return "warning";
    if (hoursDiff > -1) return "due";
    return "normal";
  }, []);

  // Memoized due words calculation
  const getDueWords = useCallback(() => {
    const now = new Date();
    return vocabularyList.filter((word) => new Date(word.nextReview) <= now);
  }, [vocabularyList]);

  // Memoized priority functions
  const getPriorityColor = useCallback((priority) => {
    switch (priority) {
      case "urgent":
        return "border-red-400/70 hover:border-red-400 hover:shadow-neon-pink";
      case "warning":
        return "border-yellow-400/70 hover:border-yellow-400 hover:shadow-neon-green";
      case "due":
        return "border-blue-400/70 hover:border-blue-400 hover:shadow-neon-cyan";
      default:
        return "border-green-400/70 hover:border-green-400 hover:shadow-neon-green";
    }
  }, []);

  const getPriorityIcon = useCallback((priority) => {
    switch (priority) {
      case "urgent":
        return "🚨";
      case "warning":
        return "⚠️";
      case "due":
        return "⏰";
      default:
        return "✅";
    }
  }, []);

  const getStepDescription = useCallback((step) => {
    const descriptions = [
      "Từ mới",
      "20 phút",
      "1 giờ",
      "4 giờ",
      "8 giờ",
      "1 ngày",
      "3 ngày",
      "1 tuần",
      "2 tuần",
      "1 tháng",
      "3 tháng",
      "6 tháng",
    ];
    return descriptions[step] || "Lâu dài";
  }, []);

  // Audio functions with cleanup optimization
  const stopAllAudio = useCallback(() => {
    // Clear any existing timeouts
    if (window.sentenceAudioTimeout) {
      clearTimeout(window.sentenceAudioTimeout);
      window.sentenceAudioTimeout = null;
    }
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
      audioTimeoutRef.current = null;
    }

    // Stop all audio elements
    if (audioVocabRef.current) {
      audioVocabRef.current.pause();
      audioVocabRef.current.currentTime = 0;
      audioVocabRef.current.src = "";
    }
    if (audioSentenceRef.current) {
      audioSentenceRef.current.pause();
      audioSentenceRef.current.currentTime = 0;
      audioSentenceRef.current.src = "";
    }

    // Stop YouTube player
    if (youtubePlayerRef.current && youtubePlayerRef.current.pauseVideo) {
      try {
        youtubePlayerRef.current.pauseVideo();
        youtubePlayerRef.current.stopVideo();
      } catch (error) {
        console.log("Error stopping YouTube player:", error);
      }
    }

    // Stop speech synthesis
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }

    // Stop any other audio elements
    try {
      const allAudio = document.querySelectorAll("audio");
      allAudio.forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
      });
    } catch (error) {
      console.log("Error stopping other audio elements:", error);
    }
  }, []);

  // Optimized audio functions
  const playGoogleTTS = useCallback((text) => {
    if ("speechSynthesis" in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = "ja-JP"; // Changed to Japanese
      utterance.rate = 0.8;
      utterance.pitch = 1;
      utterance.volume = 1;

      const voices = window.speechSynthesis.getVoices();
      const japaneseVoice =
        voices.find(
          (voice) =>
            voice.lang.startsWith("ja") &&
            (voice.name.includes("Google") || voice.name.includes("Microsoft") || voice.name.includes("Japanese"))
        ) || voices.find((voice) => voice.lang.startsWith("ja"));

      if (japaneseVoice) {
        utterance.voice = japaneseVoice;
      }

      window.speechSynthesis.speak(utterance);
    } else {
      console.warn("Speech synthesis not supported in this browser");
      alert("Trình duyệt không hỗ trợ đọc văn bản. Vui lòng thêm link audio.");
    }
  }, []);

  const playVocabAudio = useCallback((audioUrl, wordText = "") => {
    stopAllAudio();
    if (audioUrl) {
      if (audioVocabRef.current) {
        audioVocabRef.current.src = audioUrl;
        audioVocabRef.current.play().catch(console.error);
      }
    } else if (wordText) {
      playGoogleTTS(wordText);
    }
  }, [stopAllAudio, playGoogleTTS]);

  // YouTube optimized functions
  const extractYouTubeVideoId = useCallback((url) => {
    const regex =
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  }, []);

  const isYouTubeUrl = useCallback((url) => {
    return url.includes("youtube.com") || url.includes("youtu.be");
  }, []);

  const playYouTubeSegment = useCallback(
    (videoId, startTime, endTime) => {
      stopAllAudio();
      if (youtubePlayerRef.current && youtubePlayerRef.current.destroy) {
        try {
          youtubePlayerRef.current.destroy();
        } catch (error) {
          console.log("Error destroying YouTube player:", error);
        }
      }

      if (window.YT && window.YT.Player) {
        youtubePlayerRef.current = new window.YT.Player("youtube-player", {
          height: "0",
          width: "0",
          videoId: videoId,
          playerVars: {
            start: Math.floor(startTime),
            end: endTime > startTime ? Math.floor(endTime) : undefined,
            autoplay: 1,
          },
        });
      }
    },
    [stopAllAudio]
  );

  const playSentenceAudio = useCallback(
    (audioUrl, startTime = 0, endTime = 0) => {
      if (!audioUrl) return;
      stopAllAudio();

      if (isYouTubeUrl(audioUrl)) {
        const videoId = extractYouTubeVideoId(audioUrl);
        if (videoId) {
          playYouTubeSegment(videoId, startTime, endTime);
        }
      } else {
        if (audioSentenceRef.current) {
          audioSentenceRef.current.src = audioUrl;
          audioSentenceRef.current.currentTime = startTime;

          if (window.sentenceAudioTimeout) {
            clearTimeout(window.sentenceAudioTimeout);
            window.sentenceAudioTimeout = null;
          }

          audioSentenceRef.current.play().catch(console.error);

          if (endTime > startTime) {
            window.sentenceAudioTimeout = setTimeout(() => {
              if (audioSentenceRef.current) {
                audioSentenceRef.current.pause();
              }
              window.sentenceAudioTimeout = null;
            }, (endTime - startTime) * 1000);
          }
        }
      }
    },
    [stopAllAudio, isYouTubeUrl, extractYouTubeVideoId, playYouTubeSegment]
  );

  const getAudioDuration = useCallback(
    (audioUrl) => {
      if (!audioUrl || isYouTubeUrl(audioUrl)) return;

      const tempAudio = new Audio();
      tempAudio.src = audioUrl;

      tempAudio.addEventListener("loadedmetadata", () => {
        if (
          tempAudio.duration &&
          !isNaN(tempAudio.duration) &&
          isFinite(tempAudio.duration)
        ) {
          const duration = Math.round(tempAudio.duration);
          console.log(`Audio duration detected: ${duration} seconds`);
        }
      });

      tempAudio.addEventListener("error", (e) => {
        console.log("Could not load audio duration:", e);
      });

      audioTimeoutRef.current = setTimeout(() => {
        if (tempAudio.readyState === 0) {
          console.log("Audio loading timeout");
        }
      }, 5000);
    },
    [isYouTubeUrl]
  );

  // Memoized filtered vocabulary with optimization
  const filteredVocabulary = useMemo(() => {
    let filteredWords = vocabularyList;

    // Apply status filter first (cheaper operation)
    if (statusFilter !== "all") {
      filteredWords = filteredWords.filter(
        (word) => getReviewPriority(word) === statusFilter
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredWords = filteredWords.filter(
        (word) =>
          word.wordJp.toLowerCase().includes(query) ||
          word.wordVi.toLowerCase().includes(query) ||
          (word.exampleSentence &&
            word.exampleSentence.toLowerCase().includes(query))
      );
    }

    // Sort only once with optimized comparison
    return filteredWords.sort((a, b) => {
      // Pre-calculate accuracy values
      const getAccuracy = (word) => {
        if (word.reviewCount === 0) return -1;
        return (word.correctCount / word.reviewCount) * 100;
      };

      const accuracyA = getAccuracy(a);
      const accuracyB = getAccuracy(b);

      if (accuracyA !== accuracyB) {
        return accuracyA - accuracyB;
      }

      // Use priority order lookup
      const priorityOrder = { urgent: 1, warning: 2, due: 3, normal: 4 };
      const priorityA = priorityOrder[getReviewPriority(a)] || 5;
      const priorityB = priorityOrder[getReviewPriority(b)] || 5;

      return priorityA - priorityB;
    });
  }, [vocabularyList, statusFilter, searchQuery, getReviewPriority]);

  // Vocabulary management functions (optimized with immediate save)
  const handleAddVocabulary = useCallback(
    (wordData, isEdit, editWord) => {
      let wordId;
      if (isEdit && editWord) {
        wordId = editWord.id;
      } else {
        const baseId = wordData.wordJp
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/g, ""); // Allow Japanese characters
        const timestamp = Date.now();
        const randomSuffix = Math.random().toString(36).substring(2, 8);
        wordId = `${baseId}-${timestamp}-${randomSuffix}`;

        while (vocabularyList.find((word) => word.id === wordId)) {
          const newRandomSuffix = Math.random().toString(36).substring(2, 8);
          wordId = `${baseId}-${timestamp}-${newRandomSuffix}`;
        }
      }

      if (!isEdit) {
        const existingWordById = vocabularyList.find(
          (word) => word.id === wordId
        );
        if (existingWordById) {
          alert(`Lỗi: ID từ vựng bị trùng lặp. Vui lòng thử lại.`);
          return;
        }

        const similarWord = vocabularyList.find(
          (word) =>
            word.wordJp.toLowerCase() === wordData.wordJp.toLowerCase() &&
            word.wordVi.toLowerCase() === wordData.wordVi.toLowerCase()
        );

        if (
          similarWord &&
          !window.confirm(
            `Có vẻ như từ "${wordData.wordJp}" với nghĩa "${wordData.wordVi}" đã tồn tại. Bạn có chắc muốn thêm từ tương tự không?`
          )
        ) {
          return;
        }
      }

      const finalWordData = {
        id: wordId,
        ...wordData,
        createdAt: isEdit && editWord ? editWord.createdAt : new Date(),
        reviewCount: isEdit && editWord ? editWord.reviewCount : 0,
        correctCount: isEdit && editWord ? editWord.correctCount : 0,
        nextReview: isEdit && editWord ? editWord.nextReview : new Date(),
        difficulty: isEdit && editWord ? editWord.difficulty : "medium",
        step: isEdit && editWord ? editWord.step : 0,
        intervalHours: isEdit && editWord ? editWord.intervalHours : 0,
      };

      if (isEdit && editWord) {
        const updatedList = vocabularyList.map((word) => 
          word.id === editWord.id ? finalWordData : word
        );
        setVocabularyList(updatedList);
        // Save immediately after edit
        saveToLocalStorage(updatedList);
        alert("Đã cập nhật từ vựng thành công!");
      } else {
        const newList = [...vocabularyList, finalWordData];
        setVocabularyList(newList);
        setHiddenVietnameseMeanings((prev) => new Set([...prev, wordId]));
        // Save immediately after add
        saveToLocalStorage(newList);
        alert("Đã thêm từ vựng thành công!");
      }
    },
    [vocabularyList, saveToLocalStorage]
  );

  const handleUpdateVocabulary = useCallback((updatedWord) => {
    const updatedList = vocabularyList.map((word) => 
      word.id === updatedWord.id ? updatedWord : word
    );
    setVocabularyList(updatedList);
    // Save immediately after review/test complete
    saveToLocalStorage(updatedList);
  }, [vocabularyList, saveToLocalStorage]);

  const highlightSearchText = useCallback((text, search) => {
    if (!search.trim()) return text;

    const regex = new RegExp(
      `(${search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark
          key={index}
          className="bg-yellow-200 text-yellow-900 rounded px-1"
        >
          {part}
        </mark>
      ) : (
        part
      )
    );
  }, []);

  const editVocabulary = useCallback((word) => {
    setIsEditMode(true);
    setEditingWord(word);
    setShowAddForm(true);
  }, []);

  const deleteVocabulary = useCallback((id) => {
    if (window.confirm("Bạn có chắc muốn xóa từ vựng này?")) {
      const filteredList = vocabularyList.filter((word) => word.id !== id);
      setVocabularyList(filteredList);
      // Save immediately after delete
      saveToLocalStorage(filteredList);
    }
  }, [vocabularyList, saveToLocalStorage]);

  const deleteAllVocabulary = useCallback(() => {
    if (
      window.confirm(
        "Bạn có chắc chắn muốn xóa TẤT CẢ từ vựng?\n\nHành động này sẽ:\n• Xóa tất cả từ vựng đã lưu\n• Xóa dữ liệu lưu trữ\n• Không thể hoàn tác\n\nNhấn OK để xác nhận."
      )
    ) {
      setVocabularyList([]);
      // Save immediately after delete all (will remove from localStorage)
      saveToLocalStorage([]);
      alert("Đã xóa tất cả từ vựng thành công!");
    }
  }, [saveToLocalStorage]);

  const toggleVietnameseMeaning = useCallback((wordId) => {
    setHiddenVietnameseMeanings((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(wordId)) {
        newSet.delete(wordId);
      } else {
        newSet.add(wordId);
      }
      return newSet;
    });
  }, []);

  const toggleAllVietnameseMeanings = useCallback(() => {
    const currentVocab = filteredVocabulary;
    const allWordIds = currentVocab.map((word) => word.id);
    const allHidden = allWordIds.every((id) =>
      hiddenVietnameseMeanings.has(id)
    );

    if (allHidden) {
      setHiddenVietnameseMeanings(new Set());
    } else {
      setHiddenVietnameseMeanings(new Set(allWordIds));
    }
  }, [filteredVocabulary, hiddenVietnameseMeanings]);

  // Export/Import functions (optimized)
  const exportVocabulary = useCallback(() => {
    if (vocabularyList.length === 0) {
      alert("Không có từ vựng nào để xuất");
      return;
    }

    const exportData = vocabularyList.map((word) => ({
      id: word.id,
      wordJp: word.wordJp,
      wordVi: word.wordVi,
      audioVocab: word.audioVocab,
      audioSentence: word.audioSentence,
      sentenceStartTime: word.sentenceStartTime,
      sentenceEndTime: word.sentenceEndTime,
      exampleSentence: word.exampleSentence,
      imageUrl: word.imageUrl,
      createdAt: word.createdAt,
      reviewCount: word.reviewCount,
      correctCount: word.correctCount,
      nextReview: word.nextReview,
      difficulty: word.difficulty,
      step: word.step,
      intervalHours: word.intervalHours,
    }));

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `vocabulary-japanese-${
      new Date().toISOString().split("T")[0]
    }.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [vocabularyList]);

  const importVocabulary = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target.result);
          if (Array.isArray(importedData)) {
            let addedCount = 0;
            let updatedCount = 0;
            let skippedCount = 0;
            const processedData = [];
            let updatedList = [...vocabularyList];

            importedData.forEach((importWord) => {
              const existingWord = vocabularyList.find(
                (word) =>
                  word.wordJp.toLowerCase() ===
                    importWord.wordJp.toLowerCase() ||
                  (importWord.id && word.id === importWord.id)
              );

              if (existingWord) {
                const action = window.confirm(
                  `Từ "${importWord.wordJp}" đã tồn tại. Nhấn OK để cập nhật, Cancel để bỏ qua.`
                );
                if (action) {
                  const updatedWord = {
                    ...existingWord,
                    ...importWord,
                    id: existingWord.id,
                    createdAt: new Date(existingWord.createdAt),
                    nextReview: new Date(
                      importWord.nextReview || existingWord.nextReview
                    ),
                  };
                  updatedList = updatedList.map((word) =>
                    word.id === existingWord.id ? updatedWord : word
                  );
                  updatedCount++;
                } else {
                  skippedCount++;
                }
              } else {
                const newWord = {
                  ...importWord,
                  id:
                    importWord.id ||
                    `${importWord.wordJp
                      .toLowerCase()
                      .replace(
                        /[^a-z0-9\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/g,
                        ""
                      )}-${Date.now()}-${Math.random()}`,
                  createdAt: new Date(importWord.createdAt || Date.now()),
                  nextReview: new Date(importWord.nextReview || Date.now()),
                };
                processedData.push(newWord);
                addedCount++;
              }
            });

            if (processedData.length > 0) {
              updatedList = [...updatedList, ...processedData];
              const newWordIds = processedData.map((word) => word.id);
              setHiddenVietnameseMeanings(
                (prev) => new Set([...prev, ...newWordIds])
              );
            }

            // Update state and save immediately after import
            setVocabularyList(updatedList);
            saveToLocalStorage(updatedList);

            let message = `Kết quả nhập dữ liệu:\n`;
            message += `• Thêm mới: ${addedCount} từ\n`;
            message += `• Cập nhật: ${updatedCount} từ\n`;
            message += `• Bỏ qua: ${skippedCount} từ`;
            alert(message);
          } else {
            alert("Định dạng file không đúng");
          }
        } catch (error) {
          alert("Lỗi khi đọc file: " + error.message);
        }
      };
      reader.readAsText(file);
      event.target.value = "";
    },
    [vocabularyList, saveToLocalStorage]
  );

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="min-h-screen bg-black overflow-hidden relative pt-16">
      {/* Simplified Background Elements - Reduced Animations */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        {/* Removed animate-pulse-neon for better performance */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-cyan-400/10 to-blue-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-br from-pink-400/10 to-purple-600/10 rounded-full blur-3xl"></div>
        {/* Simplified grid background - removed animation */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(cyan 1px, transparent 1px), linear-gradient(90deg, cyan 1px, transparent 1px)`,
            backgroundSize: "100px 100px",
            opacity: 0.05,
          }}
        ></div>
      </div>

      {/* Optimized CSS Styles - Reduced Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        /* Removed pulse-neon, gridPulse, gradient-x animations for performance */
        @keyframes blink {
          0%,
          50% {
            opacity: 1;
          }
          51%,
          100% {
            opacity: 0;
          }
        }
        /* Simplified border-pulse with lower frequency */
        @keyframes border-pulse {
          0%,
          100% {
            opacity: 0.4;
          }
          50% {
            opacity: 0.8;
          }
        }
        @keyframes slide-right {
          0% {
            transform: translateX(-100%);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes gradient-x {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
        .animate-blink {
          animation: blink 1s step-start infinite;
        }
        /* Reduced animation frequency for better performance */
        .animate-border-pulse {
          animation: border-pulse 4s ease-in-out infinite;
        }
        .animate-slide-right {
          animation: slide-right 1s ease-out;
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }

        /* Reduced shadow intensity for better performance */
        .shadow-neon-cyan {
          box-shadow: 0 0 15px rgba(0, 255, 255, 0.3),
            0 0 25px rgba(0, 255, 255, 0.2);
        }
        .shadow-neon-cyan-intense {
          box-shadow: 0 0 20px rgba(0, 255, 255, 0.5),
            0 0 35px rgba(0, 255, 255, 0.3);
        }
        .shadow-neon-pink {
          box-shadow: 0 0 15px rgba(255, 0, 128, 0.3),
            0 0 25px rgba(255, 0, 128, 0.2);
        }
        .shadow-neon-pink-intense {
          box-shadow: 0 0 20px rgba(255, 0, 128, 0.5),
            0 0 35px rgba(255, 0, 128, 0.3);
        }
        .shadow-neon-green {
          box-shadow: 0 0 15px rgba(0, 255, 128, 0.3),
            0 0 25px rgba(0, 255, 128, 0.2);
        }
        .shadow-neon-green-intense {
          box-shadow: 0 0 20px rgba(0, 255, 128, 0.5),
            0 0 35px rgba(0, 255, 128, 0.3);
        }
        .shadow-neon-purple {
          box-shadow: 0 0 15px rgba(128, 0, 255, 0.3),
            0 0 25px rgba(128, 0, 255, 0.2);
        }
        .shadow-neon-orange {
          box-shadow: 0 0 15px rgba(255, 165, 0, 0.3),
            0 0 25px rgba(255, 165, 0, 0.2);
        }
        .shadow-neon-orange-intense {
          box-shadow: 0 0 20px rgba(255, 165, 0, 0.5),
            0 0 35px rgba(255, 165, 0, 0.3);
        }

        .delay-1000 {
          animation-delay: 1s;
        }
      `}</style>

      {/* Hidden audio elements */}
      <audio ref={audioVocabRef} />
      <audio ref={audioSentenceRef} />
      <div id="youtube-player" style={{ display: "none" }}></div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Add Vocabulary Form */}
        <AddVocabularyForm
          showAddForm={showAddForm}
          setShowAddForm={setShowAddForm}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          editingWord={editingWord}
          setEditingWord={setEditingWord}
          onAddVocabulary={handleAddVocabulary}
          playVocabAudio={playVocabAudio}
          playSentenceAudio={playSentenceAudio}
          stopAllAudio={stopAllAudio}
          getAudioDuration={getAudioDuration}
          extractYouTubeVideoId={extractYouTubeVideoId}
          isYouTubeUrl={isYouTubeUrl}
        />

        {/* Flashcard Modal */}
        <FlashcardModal
          showFlashcardModal={showFlashcardModal}
          setShowFlashcardModal={setShowFlashcardModal}
          flashcardModalStep={flashcardModalStep}
          setFlashcardModalStep={setFlashcardModalStep}
          vocabularyList={vocabularyList}
          getDueWords={getDueWords}
          getReviewPriority={getReviewPriority}
          onUpdateVocabulary={handleUpdateVocabulary}
          playVocabAudio={playVocabAudio}
          playSentenceAudio={playSentenceAudio}
          stopAllAudio={stopAllAudio}
          playGoogleTTS={playGoogleTTS}
          calculateNextReview={calculateNextReview}
        />

        {/* Main Content */}
        <div className="bg-black/60 backdrop-blur-sm border border-cyan-400/50 rounded-2xl shadow-neon-cyan p-6 sm:p-8 mb-8 mt-[10px] relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-cyan-400/20 to-purple-600/20 rounded-full blur-3xl"></div>

          {/* Header with action buttons */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => setShowFlashcardModal(true)}
                className="group relative px-4 sm:px-6 py-3 bg-black/60 border border-purple-400/50 text-purple-300 hover:text-purple-100 rounded-xl font-mono font-medium shadow-neon-purple hover:shadow-neon-purple transition-all duration-300 backdrop-blur-sm"
              >
                <div className="flex items-center justify-center gap-2">
                  <span>ÔN TẬP FLASHCARD</span>
                  {getDueWords().length > 0 && (
                    <div className="bg-purple-400/30 text-purple-100 px-2 py-1 rounded-lg text-xs font-bold border border-purple-400/50">
                      {getDueWords().length}
                    </div>
                  )}
                </div>
                {/* Reduced animation frequency */}
                <div className="absolute inset-0 border border-purple-400/30 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-border-pulse"></div>
              </button>

              <div className="flex gap-2 sm:gap-3 justify-between">
                <button
                  onClick={() => {
                    if (showAddForm) {
                      stopAllAudio();
                    } else {
                      setIsEditMode(false);
                      setEditingWord(null);
                    }
                    setShowAddForm(!showAddForm);
                  }}
                  className="group flex items-center justify-center gap-2 px-3 sm:px-4 py-3 bg-black/60 border border-blue-400/50 text-blue-300 hover:text-blue-100 rounded-xl font-mono font-medium shadow-neon-green hover:shadow-neon-green-intense transition-all duration-300 backdrop-blur-sm"
                >
                  {showAddForm ? (
                    "ĐÓNG FORM"
                  ) : isEditMode ? (
                    "CHẾ ĐỘ SỬA"
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                      className="size-5"
                    >
                      <path d="M8.75 3.75a.75.75 0 0 0-1.5 0v3.5h-3.5a.75.75 0 0 0 0 1.5h3.5v3.5a.75.75 0 0 0 1.5 0v-3.5h3.5a.75.75 0 0 0 0-1.5h-3.5v-3.5Z" />
                    </svg>
                  )}
                </button>

                <button
                  onClick={exportVocabulary}
                  className="group flex items-center justify-center gap-2 px-3 sm:px-4 py-3 bg-black/60 border border-green-400/50 text-green-300 hover:text-green-100 rounded-xl font-mono font-medium shadow-neon-green hover:shadow-neon-green-intense transition-all duration-300 backdrop-blur-sm"
                  title="[XUẤT DỮ LIỆU]"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                      className="w-5 h-5"
                    >
                      <path d="M8.75 2.75a.75.75 0 0 0-1.5 0v5.69L5.03 6.22a.75.75 0 0 0-1.06 1.06l3.5 3.5a.75.75 0 0 0 1.06 0l3.5-3.5a.75.75 0 0 0-1.06-1.06L8.75 8.44V2.75Z" />
                      <path d="M3.5 9.75a.75.75 0 0 0-1.5 0v1.5A2.75 2.75 0 0 0 4.75 14h6.5A2.75 2.75 0 0 0 14 11.25v-1.5a.75.75 0 0 0-1.5 0v1.5c0 .69-.56 1.25-1.25 1.25h-6.5c-.69 0-1.25-.56-1.25-1.25v-1.5Z" />
                    </svg>
                  </div>
                  {/* <span className="hidden sm:inline">EXPORT</span> */}
                </button>

                <label
                  className="group flex items-center justify-center gap-2 px-3 sm:px-4 py-3 bg-black/60 border border-pink-400/50 text-pink-300 hover:text-pink-100 rounded-xl font-mono font-medium shadow-neon-pink hover:shadow-neon-pink-intense transition-all duration-300 cursor-pointer backdrop-blur-sm"
                  title="[NHẬP DỮ LIỆU]"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                      className="w-5 h-5"
                    >
                      <path d="M7.25 10.25a.75.75 0 0 0 1.5 0V4.56l2.22 2.22a.75.75 0 1 0 1.06-1.06l-3.5-3.5a.75.75 0 0 0-1.06 0l-3.5 3.5a.75.75 0 0 0 1.06 1.06l2.22-2.22v5.69Z" />
                      <path d="M3.5 9.75a.75.75 0 0 0-1.5 0v1.5A2.75 2.75 0 0 0 4.75 14h6.5A2.75 2.75 0 0 0 14 11.25v-1.5a.75.75 0 0 0-1.5 0v1.5c0 .69-.56 1.25-1.25 1.25h-6.5c-.69 0-1.25-.56-1.25-1.25v-1.5Z" />
                    </svg>
                  </div>
                  {/* <span className="hidden sm:inline">IMPORT</span> */}
                  <input
                    type="file"
                    accept=".txt,.json"
                    onChange={importVocabulary}
                    className="hidden"
                  />
                </label>

                {vocabularyList.length > 0 && (
                  <button
                    onClick={deleteAllVocabulary}
                    className="group flex items-center justify-center gap-2 px-3 py-3 bg-black/60 border border-red-400/50 text-red-300 hover:text-red-100 rounded-xl font-mono font-medium shadow-neon-pink hover:shadow-neon-pink-intense transition-all duration-300 backdrop-blur-sm"
                    title="[XÓA TẤT CẢ]"
                  >
                    <div className="flex items-center justify-center">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            searchInputRef={searchInputRef}
            filteredCount={filteredVocabulary.length}
            totalCount={vocabularyList.length}
          />

          {/* Toggle All Vietnamese Meanings Button */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              {vocabularyList.length > 0 && (
                <button
                  onClick={toggleAllVietnameseMeanings}
                  className="flex items-center gap-2 px-4 py-2 bg-black/60 border border-green-400/50 rounded-xl text-green-300 hover:text-green-100 hover:border-green-400 hover:shadow-neon-green transition-all duration-300 backdrop-blur-sm font-mono font-bold"
                >
                  {filteredVocabulary.every((word) =>
                    hiddenVietnameseMeanings.has(word.id)
                  )
                    ? "[HIỆN TẤT CẢ VI]"
                    : "[ẨN TẤT CẢ VI]"}
                </button>
              )}
            </div>
          </div>

          {/* Statistics Grid */}
          <StatisticsGrid
            vocabularyList={vocabularyList}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            getReviewPriority={getReviewPriority}
          />

          {/* Vocabulary List */}
          <div className="space-y-2">
            {filteredVocabulary.map((word) => (
              <VocabularyCard
                key={word.id}
                word={word}
                searchQuery={searchQuery}
                hiddenVietnameseMeanings={hiddenVietnameseMeanings}
                getReviewPriority={getReviewPriority}
                getPriorityColor={getPriorityColor}
                getPriorityIcon={getPriorityIcon}
                getStepDescription={getStepDescription}
                highlightSearchText={highlightSearchText}
                playVocabAudio={playVocabAudio}
                playSentenceAudio={playSentenceAudio}
                toggleVietnameseMeaning={toggleVietnameseMeaning}
                editVocabulary={editVocabulary}
                deleteVocabulary={deleteVocabulary}
              />
            ))}

            {filteredVocabulary.length === 0 && vocabularyList.length > 0 && (
              <div className="text-center text-gray-500 py-8">
                <div className="text-4xl mb-2">🔍</div>
                <div className="text-lg font-medium mb-1">
                  Không tìm thấy từ vựng nào
                </div>
                <div className="text-sm">Thử tìm kiếm với từ khóa khác</div>
              </div>
            )}

            {vocabularyList.length === 0 && (
              <div className="text-center text-gray-500 py-8">
                <div className="text-4xl mb-2 flex justify-center items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-16 h-16 text-green-500 opacity-50"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m6 4.125 2.25 2.25m0 0 2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
                    />
                  </svg>
                </div>
                <div className="text-lg font-medium mb-1">
                  Chưa có từ vựng nào
                </div>
                <div className="text-sm">Hãy thêm từ vựng đầu tiên!</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Simplified Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-black/80 border border-cyan-400/50 text-cyan-400 hover:text-cyan-100 rounded-xl shadow-neon-cyan hover:shadow-neon-cyan-intense transition-all duration-300 group backdrop-blur-sm"
          title="[LÊN ĐẦU TRANG]"
        >
          <svg
            className="w-5 h-5 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
          {/* Removed animated border for better performance */}
        </button>
      )}
    </div>
  );
});

export default VocabularyJapanese;
